import { useState, useEffect, useRef, useMemo, useCallback } from "react";

// ─── QUESTION BANK ────────────────────────────────────────────────────────────
const QUESTIONS = [
  // ── SET 1 (Q1–Q30) ──
  {
    id: 1,
    text: 'A Registered Representative (RR) is opening a new options account for an individual. Which of the following is a mandatory component of the "Know Your Client" (KYC) process?',
    options: ["The client's educational background.", "The client's political affiliations.", "The client's risk capacity and risk tolerance.", "The client's previous three years of tax returns."],
    correct: 2,
    rationale: "KYC rules specifically require the advisor to determine a client's financial situation, investment objectives, and risk profile (capacity and tolerance).",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 2,
    text: 'A "Vulnerable Client" is defined by CIRO as someone who:',
    options: ["Has a net worth of less than $50,000.", "Is over the age of 65, regardless of health.", "Is susceptible to financial exploitation or has diminished mental capacity.", "Has never traded a derivative product before."],
    correct: 2,
    rationale: "Vulnerability is determined by cognitive health or situational susceptibility to exploitation, not strictly by age or wealth.",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 3,
    text: "A client has a total net worth of $1,200,000. However, $900,000 is tied up in their primary residence and $200,000 is in an illiquid private business. What is the client's Liquid Net Worth?",
    options: ["$1,200,000", "$300,000", "$100,000", "$1,100,000"],
    correct: 2,
    rationale: "Liquid net worth includes only assets readily converted to cash. $1,200,000 − $900,000 (home) − $200,000 (private business) = $100,000.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 4,
    text: 'A client has high "Risk Tolerance" (psychological) but very low "Risk Capacity" (financial) and wants to engage in uncovered call writing. How must the RR classify the client\'s risk profile?',
    options: ["High Risk, because tolerance is the primary driver.", "Low Risk, because capacity is the limiting factor.", "Medium Risk, as an average of the two factors.", "The RR should allow the client to decide their own classification."],
    correct: 1,
    rationale: "Suitability must be based on the lower of the client's risk tolerance and risk capacity. Even if a client wants to take risk, if they cannot afford the loss, the RR must limit the strategy.",
    topic: "Risk Profile", difficulty: "hard",
  },
  {
    id: 5,
    text: "Which document must be provided to a retail client BEFORE their first options trade is executed?",
    options: ["The Corporate Resolution.", "The Standardized Risk Disclosure Statement.", "The Hedge Agreement.", "The Monthly Statement of Account."],
    correct: 1,
    rationale: "All retail clients must receive and acknowledge the Risk Disclosure Statement, which outlines the risks of leverage and volatility.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 6,
    text: 'An "Institutional Client" (individual) is defined by CIRO as someone who beneficially owns financial assets with an aggregate realizable value that exceeds:',
    options: ["$1,000,000", "$5,000,000", "$10,000,000", "$25,000,000"],
    correct: 1,
    rationale: "For individuals, the threshold to be considered an institutional client is $5 million in financial assets.",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 7,
    text: 'Under the "Client Focused Reforms" (CFR), what is the advisor\'s primary obligation when a conflict of interest is identified between the firm and the client?',
    options: ["Disclose the conflict and proceed if the client signs a waiver.", "Resolve the conflict in the firm's favor to ensure business continuity.", "Avoid the conflict or resolve it in the client's best interest.", "Notify the Montreal Exchange of the conflict within 24 hours."],
    correct: 2,
    rationale: "The Best Interest Standard requires that the client's interests always come first when a conflict is identified.",
    topic: "Ethics & Best Interest", difficulty: "hard",
  },
  {
    id: 8,
    text: "An RR must update a client's KYC information at least every:",
    options: ["12 months.", "24 months.", "36 months.", "60 months."],
    correct: 2,
    rationale: "CIRO rules require a formal KYC update every 36 months (3 years) for all retail accounts.",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 9,
    text: 'A client opens an options account for "Hedging" purposes. Three months later, they begin trading aggressively in out-of-the-money naked puts. This constitutes:',
    options: ["A standard market evolution.", "A material change in the client's investment objectives.", "A violation of the Montreal Exchange's trading hours.", 'A "Jitney" trade.'],
    correct: 1,
    rationale: "Moving from hedging to aggressive speculation is a fundamental change in objective that requires an immediate KYC update and suitability review.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 10,
    text: 'Which of the following is NOT a requirement for a "Managed Account" in derivatives?',
    options: ["A written agreement between the firm and the client.", "Daily supervision of trades by a Designated Supervisor.", "Verbal approval from the client for every individual trade.", "A specific Investment Policy Statement (IPS)."],
    correct: 2,
    rationale: "In a Managed (Discretionary) account, the RR is authorized to trade without obtaining verbal approval for each specific order.",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 11,
    text: 'A "Trusted Contact Person" (TCP) is primarily used when an RR suspects:',
    options: ["The client is laundering money.", "The client is moving their account to a competitor.", "The client is being financially exploited or has diminished capacity.", "The client is engaging in insider trading."],
    correct: 2,
    rationale: "The TCP acts as a safe point of contact for the advisor to discuss concerns about a client's wellbeing or potential fraud.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 12,
    text: 'When a firm acts as a "Principal" in a derivative trade, it means the firm is:',
    options: ["Trading on behalf of a retail client for a commission.", "Acting as the clearinghouse (CDCC).", "Trading from its own inventory against the client.", "Managing a pension fund's assets."],
    correct: 2,
    rationale: "In a principal trade, the firm is the counterparty, meaning they are the buyer to the client's seller (or vice-versa).",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 13,
    text: "A client's annual income is $150,000. Their monthly fixed expenses are $8,000. What is their Annual Discretionary Income?",
    options: ["$150,000", "$54,000", "$96,000", "$42,000"],
    correct: 1,
    rationale: "Monthly expenses $8,000 × 12 = $96,000. $150,000 (Income) − $96,000 (Expenses) = $54,000.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 14,
    text: "If a client refuses to provide full financial disclosure for the KYC, the RR must:",
    options: ["Open the account but limit the trade size to $1,000.", "Use the information from the client's previous employer.", "Refuse to open the account for derivatives trading.", "Assume the client has 'Average' financial standing."],
    correct: 2,
    rationale: "Without complete KYC info, an advisor cannot fulfill their legal duty to ensure suitability; therefore, they cannot open the account.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 15,
    text: '"Suitability" is a concept that must be determined:',
    options: ["Only when the account is first opened.", "Once a year during the tax season.", "Before any trade is recommended or executed.", "Only if the client complains about a loss."],
    correct: 2,
    rationale: "Suitability is an ongoing, trade-by-trade obligation.",
    topic: "Suitability", difficulty: "easy",
  },
  {
    id: 16,
    text: 'An "Institutional Client" (Entity) is defined by CIRO as a non-individual with net assets of at least:',
    options: ["$5,000,000", "$10,000,000", "$25,000,000", "$100,000,000"],
    correct: 2,
    rationale: "The threshold for a non-individual (corporation/entity) to be considered institutional is $25 million in net assets.",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 17,
    text: 'A client is classified as a "Hedger" for margin purposes. This allows the client to:',
    options: ["Trade without any margin at all.", "Use lower margin rates because they own the underlying physical asset.", "Avoid paying commissions on futures trades.", "Trade only in the OTC market."],
    correct: 1,
    rationale: "Hedgers are considered lower risk by the CDCC because their physical position offsets the derivative risk, resulting in lower margin requirements.",
    topic: "Account Types", difficulty: "hard",
  },
  {
    id: 18,
    text: '"Relationship Disclosure Information" (RDI) must describe:',
    options: ["The RR's personal investment portfolio.", "The types of products and services the firm offers and the associated costs.", "The proprietary algorithms used by the Montreal Exchange.", "The names of other clients at the firm."],
    correct: 1,
    rationale: 'RDI is a "plain language" document that tells the client exactly what to expect from the firm-client relationship.',
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 19,
    text: 'Which of the following is considered a "Material Change" that requires an immediate KYC update?',
    options: ["The client moves to a new house.", "The client changes their bank branch.", "The client loses their primary source of income.", "The client switches from a PC to a Mac."],
    correct: 2,
    rationale: "A change in income level directly impacts 'Risk Capacity' and thus suitability.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 20,
    text: 'An RR recommends a "Covered Call" to a client. The client agrees and the trade is executed. Who is responsible for the suitability of this trade?',
    options: ["The client, because they agreed to it.", "The RR, because they recommended it.", "The Montreal Exchange.", "The CDCC."],
    correct: 1,
    rationale: "The primary responsibility for the suitability of a recommendation always rests with the advisor/firm.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 21,
    text: '"Fair Dealing" with clients requires the RR to:',
    options: ["Give all clients the same investment advice.", "Charge all clients the same commission rate.", "Allocate trades and opportunities fairly among all clients.", "Never allow a client to take a loss."],
    correct: 2,
    rationale: "Fair dealing focuses on equitable treatment, particularly in the allocation of profitable trades or information.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 22,
    text: 'An individual institutional client can waive the "Suitability" requirement:',
    options: ["Always.", "Never.", "Only if they sign a specific waiver and the firm agrees they are sophisticated.", "Only if they trade at least 1,000 contracts a month."],
    correct: 2,
    rationale: "Institutional individuals can waive certain suitability protections if they are deemed to have the requisite knowledge and follow the formal process.",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 23,
    text: "An RR's friend asks for the trading history of a mutual client. Sharing this information is a violation of:",
    options: ["Best Execution.", "Confidentiality and Privacy rules.", "Margin requirements.", "The Order Protection Rule."],
    correct: 1,
    rationale: "Client data is strictly confidential and can only be shared with authorized firm personnel or regulators.",
    topic: "Compliance & AML", difficulty: "hard",
  },
  {
    id: 24,
    text: 'Which of the following investment objectives is most suitable for a "Long Straddle"?',
    options: ["Steady income.", "Capital preservation.", "Speculation on high volatility.", "Tax deferral."],
    correct: 2,
    rationale: "Straddles are pure volatility bets, requiring the stock to move significantly in either direction.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 25,
    text: "A client has $500,000 in stocks and $500,000 in a savings account. They have a $400,000 mortgage as their only debt. What is their Total Net Worth?",
    options: ["$1,000,000", "$600,000", "$500,000", "$1,400,000"],
    correct: 1,
    rationale: "Assets ($500k + $500k) − Liabilities ($400k) = $600,000.",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 26,
    text: '"Know Your Product" (KYP) means the RR must:',
    options: ["Ensure the client has read the product manual.", "Understand the structure, risks, and fees of the derivative before recommending it.", "Only sell products created by their own firm.", "Guarantee that the product will not lose value."],
    correct: 1,
    rationale: "KYP is the advisor's duty to perform due diligence on the 'tools' they use for clients.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 27,
    text: "An RR who puts their own interests ahead of the client's interests is violating:",
    options: ["The Duty of Best Execution.", "The Best Interest Standard.", "The Order Protection Rule.", "The Maintenance Margin rule."],
    correct: 1,
    rationale: "This is the core ethical violation under the Client Focused Reforms.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 28,
    text: '"Risk Tolerance" is best described as:',
    options: ["The client's financial ability to lose money.", "The client's psychological willingness to take risk for a potential return.", "The client's age and health status.", "The number of years until the client retires."],
    correct: 1,
    rationale: "Tolerance is the 'emotional' side of the risk profile.",
    topic: "Risk Profile", difficulty: "easy",
  },
  {
    id: 29,
    text: "A client wants to open an account to write Naked Calls to pay for their child's university tuition starting next month. This objective is:",
    options: ["Highly suitable because of the high income potential.", "Unsuitable because it uses high-risk strategies for short-term, essential funds.", "Suitable only if the client is an institutional investor.", "Suitable if the calls are on 'Blue Chip' stocks."],
    correct: 1,
    rationale: "Using a strategy with 'unlimited risk' for a near-term, essential goal is a classic case of unsuitability.",
    topic: "Suitability", difficulty: "hard",
  },
  {
    id: 30,
    text: "The Account Application Form for derivatives must be signed by:",
    options: ["Only the client.", "Only the RR.", "The client and the RR.", "The client, the RR, and the Designated Supervisor (Branch Manager)."],
    correct: 3,
    rationale: "All three parties must sign to ensure the account has been properly vetted and supervised.",
    topic: "Documentation", difficulty: "medium",
  },
  // ── SET 2 (Q31–Q60) ──
  {
    id: 31,
    text: 'Under the "Best Interest Standard," if a Registered Representative (RR) is comparing two similar option strategies, they must recommend the one that:',
    options: ["Provides the highest commission to the RR.", "Is easiest for the firm's back office to process.", "Puts the client's interest first, considering costs and risk-adjusted returns.", "The RR has personally used in their own account."],
    correct: 2,
    rationale: "The Client Focused Reforms require that the client's interest be the priority when multiple suitable options are available.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 32,
    text: "A client has a Financial Goal of buying a house in 12 months. Which investment objective in their KYC profile would be most inconsistent with this goal?",
    options: ["Capital Preservation.", "Short-term Income.", "Aggressive Speculation.", "Liquidity."],
    correct: 2,
    rationale: "Aggressive speculation carries a high risk of total loss, which is unsuitable for a short-term, essential capital requirement.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 33,
    text: "A client reports $200,000 in stocks, $50,000 in a vehicle, $25,000 in jewelry, and $500,000 in a primary residence. They have a $10,000 credit card balance and a $350,000 mortgage. What is their Total Net Worth?",
    options: ["$200,000", "$375,000", "$415,000", "$775,000"],
    correct: 2,
    rationale: "Total Assets ($200k + $50k + $25k + $500k = $775,000) minus Total Liabilities ($10k + $350k = $360,000) = $415,000.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 34,
    text: "A client is an avid poker player and insists they are comfortable with losing their entire investment. However, their liquid assets are only $15,000. The RR should categorize the client's Risk Profile based on:",
    options: ["High Risk Tolerance (the client's attitude).", "Low Risk Capacity (the client's financial reality).", "High Risk Capacity (poker implies ability to handle swings).", "Medium Risk, as a compromise."],
    correct: 1,
    rationale: "While the client has high tolerance, they lack the capacity to absorb a loss. Suitability must be based on the more conservative of the two.",
    topic: "Risk Profile", difficulty: "medium",
  },
  {
    id: 35,
    text: "Which specific derivatives document must be updated and re-signed if a client moves from Hedging to Discretionary Trading?",
    options: ["The Risk Disclosure Statement.", "The Managed Account Agreement.", "The Corporate Resolution.", "The Margin Agreement."],
    correct: 1,
    rationale: "Discretionary trading requires a Managed Account Agreement to grant the RR the authority to trade without specific order-by-order approval.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 36,
    text: "A Registered Representative must perform a suitability determination:",
    options: ["Whenever a client deposits more than $10,000.", "Whenever a trade is recommended or a material change occurs in the client's KYC.", "Only when the client specifically asks for a risk assessment.", "At the end of every calendar year."],
    correct: 1,
    rationale: "Suitability is an 'event-driven' obligation triggered by recommendations, transfers, or life changes.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 37,
    text: '"Institutional" individual clients may waive the right to a trade-by-trade suitability determination if:',
    options: ["They have at least $1 million in net worth.", "They sign a written waiver and the dealer reasonably believes they can perform their own analysis.", "They have been trading for more than 10 years.", "They only trade index-linked derivatives."],
    correct: 1,
    rationale: "This is the 'Sophisticated Investor' carve-out for high-net-worth individuals.",
    topic: "Client Classification", difficulty: "hard",
  },
  {
    id: 38,
    text: "A client refuses to answer questions about their Annual Income during the account opening. What is the RR's required action?",
    options: ["Estimate the income based on the client's neighborhood.", "Mark it as 'N/A' and proceed with the account opening.", "Refuse to open the derivatives account.", "Ask the client to provide their spouse's income instead."],
    correct: 2,
    rationale: "Without income data, the RR cannot assess 'Risk Capacity,' making it impossible to fulfill the suitability mandate.",
    topic: "KYC & Account Opening", difficulty: "hard",
  },
  {
    id: 39,
    text: '"Know Your Product" (KYP) requirements specify that an RR must understand all of the following EXCEPT:',
    options: ["The profit potential of the derivative.", "The historical performance of the RR's other clients in that product.", "The risks and complexity of the product.", "The costs and fees associated with the product."],
    correct: 1,
    rationale: "KYP is about the product itself, not the performance of other clients.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 40,
    text: 'A "Conflict of Interest" is considered "Material" if it:',
    options: ["Involves more than $100 in potential profit for the RR.", "Could reasonably be expected to influence either the RR's recommendation or the client's decision.", "Is disclosed in the Relationship Disclosure Information (RDI).", "Only occurs once per year."],
    correct: 1,
    rationale: "Materiality is based on the potential to sway the judgment of either the advisor or the client.",
    topic: "Conflicts of Interest", difficulty: "medium",
  },
  {
    id: 41,
    text: "A client wants to open an account to generate income from their existing $1 million stock portfolio. They have no prior experience in derivatives. The RR should:",
    options: ["Immediately approve them for naked put writing.", "Recommend a conservative strategy like Covered Calls and provide the Risk Disclosure Statement.", "Tell the client that income generation is not possible with derivatives.", "Wait for the client to trade for one year in a cash account first."],
    correct: 1,
    rationale: "Covered calls are a standard income-generating strategy for stock owners. Since the client has no experience, education and disclosure are mandatory.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 42,
    text: 'Which entity sets the "Minimum Criteria" for opening a derivatives account in Canada?',
    options: ["The Bank of Canada.", "CIRO.", "The TSX.", "The Federal Ministry of Finance."],
    correct: 1,
    rationale: "CIRO (formerly IIROC) is the SRO that establishes the conduct and account opening standards.",
    topic: "Compliance & AML", difficulty: "easy",
  },
  {
    id: 43,
    text: 'A client has $500,000 in total assets and $600,000 in total liabilities. Their "Net Worth" is:',
    options: ["$1,100,000", "$100,000", "−$100,000", "$0"],
    correct: 2,
    rationale: "Net Worth = Assets ($500k) − Liabilities ($600k) = −$100,000.",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 44,
    text: '"Relationship Disclosure Information" (RDI) must be provided to a client:',
    options: ["Verbally over the phone.", "In writing, at the time of account opening.", "Only if the client requests it in writing.", "By the Montreal Exchange, not the dealer."],
    correct: 1,
    rationale: "RDI is part of the initial account opening package.",
    topic: "Documentation", difficulty: "easy",
  },
  {
    id: 45,
    text: 'When identifying a "Trusted Contact Person" (TCP), the client is giving the RR permission to:',
    options: ["Let the TCP trade on the account.", "Transfer funds to the TCP in an emergency.", "Contact the TCP if the RR suspects financial exploitation or cognitive decline.", "Share the client's monthly statements with the TCP."],
    correct: 2,
    rationale: "The TCP is a protective measure, not a grant of trading or withdrawal authority.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 46,
    text: "An RR who encourages a client to trade excessively just to generate commissions is guilty of:",
    options: ["Front-running.", "Churning.", "Spoofing.", "Scalping."],
    correct: 1,
    rationale: "Churning is a violation of the duty of fair dealing and the best interest standard.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 47,
    text: "Which of the following is an Institutional Client by CIRO's definition?",
    options: ["A student with a $10,000 portfolio.", "A Canadian municipality.", "A private club with $100,000 in net assets.", "An individual with $2 million in financial assets."],
    correct: 1,
    rationale: "Municipalities, governments, and large corporations are classified as institutional clients.",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 48,
    text: 'A client signs a "Waiver of Suitability" for a specific trade. Does this exempt the RR from their regulatory duty?',
    options: ["Yes, if the client is wealthy.", "Yes, if the client is an institutional entity.", "No, retail suitability cannot be waived by a signature.", "Yes, if the trade is in the OTC market."],
    correct: 2,
    rationale: "Retail clients cannot 'contract out' of the protections provided by suitability rules.",
    topic: "Suitability", difficulty: "hard",
  },
  {
    id: 49,
    text: '"Client Focused Reforms" require firms to evaluate "Product Costs" when making a recommendation. This means the RR should:',
    options: ["Always recommend the cheapest product.", "Consider whether lower-cost alternatives would meet the client's needs just as well.", "Only recommend products with zero commissions.", "Ignore costs if the profit potential is high."],
    correct: 1,
    rationale: "Cost is one of many factors in the best interest determination.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 50,
    text: 'The RR\'s "Gatekeeper" role includes reporting:',
    options: ["Clients who don't pay their commissions on time.", "Suspicious transactions that might indicate money laundering or market manipulation.", "Clients who complain about the weather.", "Colleagues who take longer than an hour for lunch."],
    correct: 1,
    rationale: "Gatekeepers protect the integrity of the financial system from abuse.",
    topic: "Compliance & AML", difficulty: "medium",
  },
  {
    id: 51,
    text: "A client earns $100,000/year and spends $40,000 on taxes and $40,000 on living expenses. Their Risk Capacity for a total loss in a given year is approximately:",
    options: ["$100,000", "$60,000", "$20,000", "$0"],
    correct: 2,
    rationale: "Capacity is based on what is left over after essential obligations. $100k − $40k (Taxes) − $40k (Essentials) = $20,000.",
    topic: "Risk Profile", difficulty: "medium",
  },
  {
    id: 52,
    text: '"Proprietary Products" are those that are:',
    options: ["Traded only on the Montreal Exchange.", "Created or managed by the RR's own firm.", "Guaranteed by the CDCC.", "Traded by all retail investors."],
    correct: 1,
    rationale: "Recommending proprietary products often creates a conflict of interest that must be managed.",
    topic: "Conflicts of Interest", difficulty: "medium",
  },
  {
    id: 53,
    text: "A client is extremely upset about an options loss and tells the RR they are going to get even with the market. The RR should:",
    options: ["Encourage the client to double their next trade size.", "Re-evaluate the client's risk tolerance and potentially suggest they stop trading.", "Lend the client money to keep trading.", "Offer the client a job at the brokerage."],
    correct: 1,
    rationale: "Significant emotional distress can change a client's risk profile and indicates they may no longer be suitable for derivatives.",
    topic: "Risk Profile", difficulty: "medium",
  },
  {
    id: 54,
    text: 'When an account is "Cash-Secured," it means:',
    options: ["The client has deposited enough cash to pay for the full value of the underlying stock if assigned.", "The client has a large savings account at a different bank.", "The RR is personally guaranteeing the trade with cash.", "The CDCC has waived the margin requirement."],
    correct: 0,
    rationale: "This is common for selling puts; it reduces the risk because the money to buy the stock is already in the account.",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 55,
    text: "Which of the following is a Material Conflict that must be disclosed to the client?",
    options: ["The RR's firm receives a higher commission for selling certain options.", "The RR's firm is also a market maker in the security being recommended.", "The RR is a shareholder in the firm they work for.", "All of the above."],
    correct: 3,
    rationale: "Any situation where the firm or advisor has an interest that could conflict with the client's best interest must be disclosed.",
    topic: "Conflicts of Interest", difficulty: "medium",
  },
  {
    id: 56,
    text: 'A "Limited Power of Attorney" (LPOA) for a derivatives account allows an RR to:',
    options: ["Close the account and take the money.", "Place trades without specific client approval (if the firm allows discretionary trading).", "Change the client's home address.", "Sign the client's tax returns."],
    correct: 1,
    rationale: "LPOAs are the legal mechanism for granting trading authority.",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 57,
    text: '"Standard of Care" refers to:',
    options: ["The minimum level of service required by law.", "The RR's duty to act with care, honesty, and in the best interest of the client.", "The cleanliness of the brokerage office.", "The speed of the electronic trading system."],
    correct: 1,
    rationale: "This is the ethical foundation of the advisor-client relationship.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 58,
    text: "An RR is moving from one firm to another. They want to bring their client's KYC data with them. They can do this only if:",
    options: ["The new firm pays for the data.", "The client provides written consent.", "The data is more than 3 years old.", "The Montreal Exchange approves the transfer."],
    correct: 1,
    rationale: "Client privacy rules prohibit the transfer of personal data between firms without explicit client permission.",
    topic: "Compliance & AML", difficulty: "hard",
  },
  {
    id: 59,
    text: 'Which of the following defines "Investment Objectives"?',
    options: ["The client's desire to retire at age 55.", "The client's preference for trading tech stocks.", "The client's need for income, growth, or capital preservation.", "The client's total net worth."],
    correct: 2,
    rationale: "Objectives describe what the client hopes the money will do (e.g., generate income).",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 60,
    text: '"Secondary KYC" refers to:',
    options: ["Checking a client's social media.", "Verifying the client's identity through a passport or driver's license.", "Updating the KYC for a second time in one year.", "Asking for a second opinion on suitability."],
    correct: 1,
    rationale: "Verification of identity is a separate but related step to the financial KYC process.",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  // ── SET 3 (Q61–Q90) ──
  {
    id: 61,
    text: 'An RR is opening an account for a client who wishes to engage in "Spreading." What is a mandatory prerequisite before the first spread trade can be executed?',
    options: ["The client must have at least five years of stock trading experience.", "The RR must ensure the client has been provided with the most recent CDCC Information Summary.", "The client must deposit at least $25,000 in cash.", "The Montreal Exchange must approve the specific account number."],
    correct: 1,
    rationale: "Before trading any cleared derivative, the dealer must provide the client with a copy of the risk disclosure or the information summary provided by the clearinghouse (CDCC).",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 62,
    text: 'Which of the following is considered a "Non-Individual" institutional client?',
    options: ["A student union with $1 million in assets.", "A Canadian trust company.", "A small business with three employees and $2 million in revenue.", "An RR's personal holding company with $500,000 in cash."],
    correct: 1,
    rationale: "Trust companies, banks, and insurance companies are 'institutional' by virtue of their regulated status.",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 63,
    text: "A client has $800,000 in a retirement account (RRSP) and $200,000 in a non-registered margin account. They have a $50,000 line of credit and $20,000 in credit card debt. What is the client's Financial Wealth for institutional classification purposes?",
    options: ["$1,000,000", "$930,000", "$730,000", "$200,000"],
    correct: 1,
    rationale: "Financial wealth = total financial assets ($800k + $200k = $1M) minus total financial liabilities ($50k + $20k = $70k) = $930,000.",
    topic: "KYC & Account Opening", difficulty: "hard",
  },
  {
    id: 64,
    text: 'A client wants to trade futures but refuses to sign the "Hedge Agreement." How should the RR handle the account\'s margin requirements?',
    options: ["Treat the client as a speculator and apply standard (higher) margin rates.", "Refuse to open the futures account.", "Apply hedger rates anyway if the client mentions they own the physical asset.", "Ask the client to provide their tax returns instead of the agreement."],
    correct: 0,
    rationale: "Without a signed Hedge Agreement, a dealer cannot legally apply reduced 'hedger' margin rates. The client must be treated as a speculator.",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 65,
    text: '"Personal Financial Dealings" with a client include all of the following EXCEPT:',
    options: ["Borrowing money from a client.", "Co-signing a loan for a client.", "Charging a client a standard commission for an options trade.", "Acting as a beneficiary for a client's estate (who is not a relative)."],
    correct: 2,
    rationale: "Standard business transactions (like charging commissions) are not considered 'personal financial dealings.'",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 66,
    text: 'When an RR identifies that a client\'s "Risk Capacity" has significantly decreased, the RR MUST:',
    options: ["Tell the client to work harder to earn more money.", "Suggest the client switch to higher-leverage options to recover losses.", "Re-evaluate the suitability of the client's current derivative positions.", "Report the client to the firm's credit department."],
    correct: 2,
    rationale: "A drop in capacity means the client can no longer afford the same level of risk; the portfolio must be adjusted accordingly.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 67,
    text: "An individual client with $10,000,000 in financial assets is considered an:",
    options: ["Accredited Investor.", "Institutional Client.", "Retail Client with high net worth.", "Exempt Market Dealer."],
    correct: 1,
    rationale: "Under CIRO rules, an individual with more than $5 million in financial assets meets the definition of an 'Institutional Client.'",
    topic: "Client Classification", difficulty: "medium",
  },
  {
    id: 68,
    text: 'A "Managed Account" in derivatives requires a Designated Supervisor to review all trades:',
    options: ["On a monthly basis.", "On a weekly basis.", "On a daily basis.", "Only when the account value drops by more than 10%."],
    correct: 2,
    rationale: "Due to the high risk and discretionary nature, managed derivatives accounts require daily supervisory oversight.",
    topic: "Account Types", difficulty: "hard",
  },
  {
    id: 69,
    text: '"Investment Knowledge" in a KYC profile is used to determine:',
    options: ["How much the RR can charge in commissions.", "Whether the client understands the risks of the products they are trading.", "The client's eligibility for the Canadian Investor Protection Fund (CIPF).", "The client's credit score."],
    correct: 1,
    rationale: "Knowledge is a key pillar of suitability; complex products like derivatives require higher levels of client understanding.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 70,
    text: "A client wants to open an account for naked put writing. The firm's policy requires a minimum liquid net worth of $50,000 for this strategy. The client has $40,000 in cash and $20,000 in a locked-in retirement account (LIRA). Does the client meet the requirement?",
    options: ["Yes, because total liquid assets are $60,000.", "No, because a LIRA is not considered a liquid asset.", "Yes, if the RR signs a guarantee.", "No, because they need $100,000 for naked writing."],
    correct: 1,
    rationale: "'Liquid' means readily available. Retirement accounts with withdrawal restrictions (like LIRAs) are excluded from liquid net worth calculations.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 71,
    text: '"Misrepresentation" in the client relationship includes:',
    options: ["Explaining that options involve risk.", "Guaranteeing a specific profit on a futures trade.", "Disclosing the commissions before the trade.", "Advising a client to hedge their portfolio."],
    correct: 1,
    rationale: "Guaranteed returns in derivatives are impossible and constitute a fraudulent misrepresentation of the product.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 72,
    text: "An RR discovers a client is using their options account to facilitate Money Laundering. The RR's first legal obligation is to:",
    options: ["Confront the client and ask for an explanation.", "Follow the firm's Anti-Money Laundering (AML) reporting procedures.", "Liquidate the client's positions immediately.", "Call the local police department."],
    correct: 1,
    rationale: "RRs must follow internal compliance protocols, which usually involve reporting to the firm's AML officer (and FINTRAC).",
    topic: "Compliance & AML", difficulty: "medium",
  },
  {
    id: 73,
    text: "A 75-year-old client with limited income wants to day trade options for excitement. The RR's KYC assessment should prioritize:",
    options: ["The client's excitement and desire for entertainment.", "The client's limited risk capacity and income needs.", "The firm's need for higher trading volume.", "The current bull market conditions."],
    correct: 1,
    rationale: "Suitability is based on financial need and ability to lose, not the 'entertainment' value of the trades.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 74,
    text: "The Risk Disclosure Statement covers all of the following EXCEPT:",
    options: ["The risk of losing more than the initial investment in futures.", "The impact of leverage on portfolio volatility.", "The specific names of stocks the client should buy.", "The risks associated with short-selling."],
    correct: 2,
    rationale: "The disclosure statement is a general document about product risks, not a specific investment recommendation.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 75,
    text: '"Churning" is most easily identified in a client\'s account by looking for:',
    options: ["Large, one-time trades.", "A high volume of trades that seem to serve no purpose other than generating commissions.", "A client who holds a position for ten years.", "A client who only buys index-linked products."],
    correct: 1,
    rationale: "Churning is excessive trading inconsistent with the client's objectives, intended solely to benefit the RR.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 76,
    text: 'Which of the following is a "Relationship Disclosure" requirement?',
    options: ["Providing the client with the RR's home address.", "Disclosing any compensation the firm receives from third parties for the client's trades.", "Showing the client a list of the firm's most profitable clients.", "Telling the client what the RR's annual salary is."],
    correct: 1,
    rationale: "Transparency regarding third-party compensation (like payment for order flow or soft dollars) is mandatory.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 77,
    text: 'An RR is authorized to use "Discretion" in an account for one day because the client is on a plane. This is:',
    options: ["Permitted if the RR has a verbal agreement.", "Prohibited unless the account is formally opened as a Managed Account.", "Permitted if the RR's manager approves.", "Permitted only if the trade is a hedge."],
    correct: 1,
    rationale: "'Temporary discretion' is not recognized in the derivatives industry. Discretion requires a formal, written Managed Account Agreement.",
    topic: "Account Types", difficulty: "hard",
  },
  {
    id: 78,
    text: '"Know Your Client" information must be kept for how many years after an account is closed?',
    options: ["1 year.", "2 years.", "7 years.", "Forever."],
    correct: 2,
    rationale: "Regulatory record-keeping requirements in Canada generally specify 7 years for client records.",
    topic: "Compliance & AML", difficulty: "medium",
  },
  {
    id: 79,
    text: "If a client is a Related Person to an RR (e.g., a spouse), the account is considered:",
    options: ["An Institutional Account.", "A Pro Account (Professional/Employee-related).", "An Exempt Account.", "A Managed Account."],
    correct: 1,
    rationale: "Accounts for employees and their immediate families are subject to stricter 'Pro' trading rules to prevent conflicts.",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 80,
    text: "A client has $200,000 in annual income. They want to trade naked options. The firm requires that no more than 10% of annual income be at risk in any single trade. What is the client's maximum At-Risk amount per trade?",
    options: ["$2,000", "$10,000", "$20,000", "$200,000"],
    correct: 2,
    rationale: "10% of $200,000 = $20,000.",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 81,
    text: '"Conflict of Interest" rules in the Client Focused Reforms mean an RR must:',
    options: ["Avoid all conflicts of interest at any cost.", "Identify and manage conflicts in a way that prioritizes the client's interest.", "Only disclose conflicts if the client is a retail investor.", "Ignore conflicts if they are considered 'industry standard.'"],
    correct: 1,
    rationale: "While avoidance is preferred, many conflicts can be managed through disclosure and control, provided the client's interest is prioritized.",
    topic: "Conflicts of Interest", difficulty: "medium",
  },
  {
    id: 82,
    text: "An RR should review a client's derivatives account Suitability if:",
    options: ["The market goes up by 1%.", "The client moves from Toronto to Vancouver.", "The RR believes the client's risk tolerance may have changed based on recent interactions.", "The RR's firm changes its logo."],
    correct: 2,
    rationale: "Any indication of a change in risk profile (even if not formally reported) should trigger a suitability discussion.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 83,
    text: "A client wants to use their Emergency Fund to speculate on index futures. The RR knows this fund is for the client's medical expenses. The RR should:",
    options: ["Allow the trade because it's the client's money.", "Advise against it and document that the trade is unsuitable for an emergency fund.", "Charge a higher commission to discourage the client.", "Open the account but only for one day."],
    correct: 1,
    rationale: "Emergency funds require liquidity and capital preservation; speculation is fundamentally at odds with this objective.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 84,
    text: "Which of the following is an Investment Objective?",
    options: ["Owning a boat.", "Tax Minimization.", "Trading 10 times a day.", "Capital Appreciation."],
    correct: 3,
    rationale: "Objectives describe the desired financial outcome (e.g., growing the principal).",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 85,
    text: '"Account Suitability" must be determined at which level?',
    options: ["Only for each individual trade.", "Only for the portfolio as a whole.", "Both for each trade and for the overall portfolio strategy.", "Only when the account is in a loss position."],
    correct: 2,
    rationale: "Suitability is holistic—it applies to the specific transaction and how that transaction fits into the broader client strategy.",
    topic: "Suitability", difficulty: "medium",
  },
  {
    id: 86,
    text: "A Registered Representative must be Registered with which body to sell derivatives?",
    options: ["The Montreal Exchange.", "CIRO.", "The Bank of Canada.", "FINTRAC."],
    correct: 1,
    rationale: "RRs must be registered with their provincial regulator and an SRO like CIRO.",
    topic: "Compliance & AML", difficulty: "easy",
  },
  {
    id: 87,
    text: '"Secondary Market KYC" is often required when:',
    options: ["A client opens a second account at the same firm.", "A dealer is facilitating an OTC derivative trade for a client.", "A client wants to trade on a US exchange.", "A client is an institutional entity with multiple sub-accounts."],
    correct: 1,
    rationale: "OTC trades often involve more complex counterparty risk assessments than exchange-traded derivatives.",
    topic: "KYC & Account Opening", difficulty: "hard",
  },
  {
    id: 88,
    text: "An RR's duty to provide Fair and Honest advice is a:",
    options: ["Fiduciary duty.", "Contractual duty.", "Regulatory duty.", "Professional courtesy."],
    correct: 2,
    rationale: "It is a core requirement of the CIRO rules governing the conduct of RRs.",
    topic: "Ethics & Best Interest", difficulty: "medium",
  },
  {
    id: 89,
    text: "A client has $1M in assets and $200k in liabilities. Their net worth is $800k. They lose $100k in a trade. Their new net worth is:",
    options: ["$700,000", "$900,000", "$800,000", "$1,000,000"],
    correct: 0,
    rationale: "$800k − $100k = $700,000.",
    topic: "KYC & Account Opening", difficulty: "easy",
  },
  {
    id: 90,
    text: 'The "Client Relationship Model" (CRM) was designed to increase:',
    options: ["The profit of brokerage firms.", "The transparency and disclosure of fees and performance to clients.", "The speed of the Canadian clearing system.", "The number of derivatives traded in Canada."],
    correct: 1,
    rationale: "CRM/CRM2 initiatives were specifically aimed at better informing the client about what they are paying and how their investments are performing.",
    topic: "Documentation", difficulty: "medium",
  },
  // ── SET 4 (Q91–Q100) ──
  {
    id: 91,
    text: "An RR is recommending a new complex derivative that was just listed on the Montreal Exchange. Even if the RR understands the product, they must ensure that:",
    options: ["The product has been approved for sale by the firm's compliance department.", "The client has already traded similar products at another firm.", "The product is guaranteed to outperform the TSX 60.", "The client's spouse also understands the product."],
    correct: 0,
    rationale: "Under KYP rules, a firm must perform its own due diligence on a product and approve it for its shelf before an RR can recommend it to a client.",
    topic: "KYC & Account Opening", difficulty: "medium",
  },
  {
    id: 92,
    text: 'If an RR suspects a client is a "Politically Exposed Person" (PEP), they must:',
    options: ["Close the account immediately.", "Notify the client that they are being monitored by the police.", "Perform enhanced due diligence (EDD) to determine the source of the client's wealth.", "Charge a 5% 'Governance Fee' on all trades."],
    correct: 2,
    rationale: "AML rules require extra scrutiny for PEPs to ensure funds are not derived from corruption or illicit activities.",
    topic: "Compliance & AML", difficulty: "medium",
  },
  {
    id: 93,
    text: "A client has $500,000 in gross annual income. They have $450,000 in total annual expenses. According to the firm's policy, a client should not lose more than 50% of their Discretionary Income in a single year of trading. What is the client's annual loss limit?",
    options: ["$25,000", "$50,000", "$225,000", "$250,000"],
    correct: 0,
    rationale: "Discretionary Income = $500,000 − $450,000 = $50,000. 50% of $50,000 = $25,000.",
    topic: "KYC & Account Opening", difficulty: "hard",
  },
  {
    id: 94,
    text: '"Client Relationship Model" (CRM) rules require that the Performance Report provided to clients must include:',
    options: ["The RR's total earnings from the account.", "The market value of the account at the beginning and end of the period.", "A comparison of the account's return against a specific benchmark.", "The personal home address of the firm's CEO."],
    correct: 1,
    rationale: "CRM performance reports are designed to show the actual dollar-weighted change in account value and the total return over specific periods.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 95,
    text: "A client wants to trade derivatives in a Joint Account with their adult son. Both parties must:",
    options: ["Have identical net worths.", "Both sign the Account Application, KYC, and Risk Disclosure Statement.", "Both be registered with CIRO.", "Reside at the same address."],
    correct: 1,
    rationale: "In a joint account, all beneficial owners are responsible for the trades and must be vetted for suitability and risk awareness.",
    topic: "Documentation", difficulty: "medium",
  },
  {
    id: 96,
    text: "A Durable Power of Attorney remains valid even if the client:",
    options: ["Becomes mentally incapacitated.", "Moves to a different province.", "Closes the account.", "Disagrees with the RR's recommendation."],
    correct: 0,
    rationale: "'Durable' or 'Enduring' means the authority survives the client's loss of mental capacity, making it a powerful tool in elder care.",
    topic: "Account Types", difficulty: "medium",
  },
  {
    id: 97,
    text: "An RR is authorized to use Discretion in an account for a client who is currently at sea for 6 months. This is only permitted if the RR's firm is specifically registered as a:",
    options: ["Portfolio Manager or Restricted Portfolio Manager.", "Investment Dealer with 'Limited Authority.'", "Mutual Fund Dealer.", "Corporate Finance House."],
    correct: 0,
    rationale: "Discretionary authority (Managed Accounts) is a specific registration category under CIRO and provincial law.",
    topic: "Account Types", difficulty: "hard",
  },
  {
    id: 98,
    text: "When an RR provides a Trade Confirmation to a client, it must include:",
    options: ["The client's original KYC profile.", "The price, quantity, and commission charged for that specific trade.", "The RR's personal opinion on the stock.", "A list of other clients who made the same trade."],
    correct: 1,
    rationale: "Confirmations are the legal record of a transaction and its associated costs.",
    topic: "Documentation", difficulty: "easy",
  },
  {
    id: 99,
    text: 'If a client is classified as a "Speculator," their investment objective is primarily:',
    options: ["Preservation of Capital.", "Short-term Income.", "Growth/Capital Appreciation through high-risk exposure.", "Long-term Bond Yields."],
    correct: 2,
    rationale: "Speculators accept a high probability of loss in exchange for the potential for high returns.",
    topic: "Client Classification", difficulty: "easy",
  },
  {
    id: 100,
    text: "The Designated Supervisor (Branch Manager) signs the account opening form to signify that:",
    options: ["They have personally met the client.", "The account meets the firm's suitability and regulatory standards.", "They will pay for any losses the client incurs.", "The Montreal Exchange has approved the client."],
    correct: 1,
    rationale: "The supervisor's signature is the final 'gatekeeping' step, ensuring the RR has performed their duties correctly.",
    topic: "Documentation", difficulty: "easy",
  },
];

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TOPICS = [
  "KYC & Account Opening",
  "Client Classification",
  "Risk Profile",
  "Suitability",
  "Ethics & Best Interest",
  "Documentation",
  "Conflicts of Interest",
  "Compliance & AML",
  "Account Types",
];

const TOPIC_COLORS = {
  "KYC & Account Opening": { bg: "#dbeafe", text: "#1e40af", bar: "#3b82f6" },
  "Client Classification": { bg: "#ccfbf1", text: "#0e7490", bar: "#06b6d4" },
  "Risk Profile": { bg: "#ede9fe", text: "#5b21b6", bar: "#8b5cf6" },
  "Suitability": { bg: "#dcfce7", text: "#166534", bar: "#22c55e" },
  "Ethics & Best Interest": { bg: "#fef3c7", text: "#92400e", bar: "#f59e0b" },
  "Documentation": { bg: "#fee2e2", text: "#991b1b", bar: "#ef4444" },
  "Conflicts of Interest": { bg: "#fce7f3", text: "#831843", bar: "#ec4899" },
  "Compliance & AML": { bg: "#f0fdf4", text: "#14532d", bar: "#16a34a" },
  "Account Types": { bg: "#f0f9ff", text: "#0c4a6e", bar: "#0ea5e9" },
};

const DIFF_COLORS = {
  easy: { bg: "#dcfce7", text: "#166534", dot: "#22c55e" },
  medium: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  hard: { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
};

const OPTION_LABELS = ["A", "B", "C", "D"];

const QUIZ_PRESETS = [
  { label: "Quick Test", count: 25, mins: 40 },
  { label: "Half Exam", count: 50, mins: 80 },
  { label: "Full Exam", count: 100, mins: 160 },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function calcScore(answers, questionList) {
  let correct = 0;
  questionList.forEach((q, i) => {
    if (answers[i] === q.correct) correct++;
  });
  return correct;
}

function topicBreakdown(answers, questionList) {
  const stats = {};
  TOPICS.forEach(t => { stats[t] = { total: 0, correct: 0 }; });
  questionList.forEach((q, i) => {
    if (!stats[q.topic]) stats[q.topic] = { total: 0, correct: 0 };
    stats[q.topic].total++;
    if (answers[i] === q.correct) stats[q.topic].correct++;
  });
  return stats;
}

function diffBreakdown(answers, questionList) {
  const stats = { easy: { t: 0, c: 0 }, medium: { t: 0, c: 0 }, hard: { t: 0, c: 0 } };
  questionList.forEach((q, i) => {
    stats[q.difficulty].t++;
    if (answers[i] === q.correct) stats[q.difficulty].c++;
  });
  return stats;
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  root: {
    minHeight: "100vh",
    background: "#f4f6f9",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#0d1b2a",
  },
  // Branding
  brand: {
    display: "flex", alignItems: "center", gap: 10,
    fontFamily: "'Georgia', serif",
  },
  brandLogo: {
    width: 36, height: 36, borderRadius: 8,
    background: "linear-gradient(135deg, #1a3050 0%, #2a4a6b 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#c9972b", fontWeight: 700, fontSize: 14, letterSpacing: 1,
    flexShrink: 0,
  },
  brandName: {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.15em",
    textTransform: "uppercase", color: "#1a3050",
    fontFamily: "Georgia, serif",
  },
  brandSub: {
    fontSize: 10, color: "#64748b", letterSpacing: "0.05em",
    fontFamily: "Georgia, serif",
  },
  // Cards
  card: {
    background: "#ffffff",
    borderRadius: 16,
    border: "1px solid #e1e8f0",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 28,
  },
  // Buttons
  btnPrimary: {
    background: "linear-gradient(135deg, #1a3050 0%, #2a4a6b 100%)",
    color: "#ffffff", border: "none", borderRadius: 10,
    padding: "12px 28px", cursor: "pointer",
    fontSize: 14, fontWeight: 600, letterSpacing: "0.03em",
    fontFamily: "Georgia, serif",
    transition: "all 0.2s",
    display: "inline-flex", alignItems: "center", gap: 8,
  },
  btnSecondary: {
    background: "#ffffff", color: "#1a3050",
    border: "1.5px solid #1a3050", borderRadius: 10,
    padding: "12px 28px", cursor: "pointer",
    fontSize: 14, fontWeight: 600, letterSpacing: "0.03em",
    fontFamily: "Georgia, serif", transition: "all 0.2s",
    display: "inline-flex", alignItems: "center", gap: 8,
  },
  btnGhost: {
    background: "transparent", color: "#526070",
    border: "1px solid #e1e8f0", borderRadius: 8,
    padding: "8px 16px", cursor: "pointer",
    fontSize: 13, fontFamily: "Georgia, serif",
    transition: "all 0.2s",
    display: "inline-flex", alignItems: "center", gap: 6,
  },
  btnGold: {
    background: "linear-gradient(135deg, #c9972b 0%, #e0b846 100%)",
    color: "#fff", border: "none", borderRadius: 10,
    padding: "12px 28px", cursor: "pointer",
    fontSize: 14, fontWeight: 700, letterSpacing: "0.05em",
    fontFamily: "Georgia, serif", transition: "all 0.2s",
    display: "inline-flex", alignItems: "center", gap: 8,
  },
  // Layout
  maxW: { maxWidth: 900, margin: "0 auto", padding: "0 20px" },
  row: { display: "flex", alignItems: "center", gap: 12 },
  // Badge
  badge: (bg, text) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    background: bg, color: text, borderRadius: 20,
    padding: "3px 10px", fontSize: 11, fontWeight: 600,
    letterSpacing: "0.05em", fontFamily: "Georgia, serif",
  }),
};

// ─── FONT LOADER ─────────────────────────────────────────────────────────────
function useFonts() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Source+Serif+4:ital,wght@0,300;0,400;0,600;1,400&family=JetBrains+Mono:wght@400;600&display=swap";
    document.head.appendChild(link);
  }, []);
}

// ─── BRAND HEADER ─────────────────────────────────────────────────────────────
function BrandHeader() {
  return (
    <div style={S.brand}>
      <div style={S.brandLogo}>V</div>
      <div>
        <div style={S.brandName}>Vitta Finance Academy</div>
        <div style={S.brandSub}>CIRO Derivatives Exam Prep</div>
      </div>
    </div>
  );
}

// ─── HOME SCREEN ─────────────────────────────────────────────────────────────
function HomeScreen({ onStart }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f4f6f9" }}>
      {/* Top nav */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #e1e8f0",
        padding: "16px 24px",
      }}>
        <div style={{ ...S.maxW, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <BrandHeader />
          <div style={{ ...S.badge("#1a3050", "#fff"), fontSize: 12, padding: "6px 14px" }}>
            Derivatives Exam
          </div>
        </div>
      </div>

      <div style={{ ...S.maxW, padding: "60px 20px" }}>
        {/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{
            display: "inline-block",
            background: "linear-gradient(135deg, #f4f6f9, #e8eef5)",
            border: "1px solid #d0dce8",
            borderRadius: 40,
            padding: "6px 20px",
            fontSize: 12, fontWeight: 700,
            letterSpacing: "0.12em", textTransform: "uppercase",
            color: "#526070", marginBottom: 20,
            fontFamily: "Georgia, serif",
          }}>
            Element 1 of 8
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(32px, 5vw, 52px)",
            fontWeight: 700, lineHeight: 1.15,
            color: "#0d1b2a", margin: "0 0 16px",
            letterSpacing: "-0.01em",
          }}>
            The Client Relationship
          </h1>
          <p style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontSize: 17, color: "#526070",
            maxWidth: 520, margin: "0 auto 28px",
            lineHeight: 1.7,
          }}>
            Account opening, KYC, client classification, suitability, and the foundational ethics of the advisor-client bond.
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
            {[
              ["100", "Questions"],
              ["9", "Topics"],
              ["3", "Difficulty Levels"],
            ].map(([n, l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 28, fontWeight: 700, color: "#c9972b", lineHeight: 1,
                }}>
                  {n}
                </div>
                <div style={{ fontSize: 11, color: "#526070", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "Georgia, serif" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mode cards */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 24, marginBottom: 40,
        }}>
          {/* Practice */}
          <div
            onClick={() => onStart("practice")}
            style={{
              ...S.card,
              cursor: "pointer",
              border: "2px solid transparent",
              transition: "all 0.25s",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.border = "2px solid #1a3050";
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(26,48,80,0.15)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.border = "2px solid transparent";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";
            }}
          >
            <div style={{
              position: "absolute", top: 0, right: 0,
              width: 80, height: 80,
              background: "linear-gradient(135deg, #f4f6f9, #e8eef5)",
              borderRadius: "0 16px 0 80px",
            }} />
            <div style={{ fontSize: 32, marginBottom: 16 }}>📚</div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 22, fontWeight: 700, margin: "0 0 10px",
              color: "#0d1b2a",
            }}>
              Practice Mode
            </h2>
            <p style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontSize: 14, color: "#526070", lineHeight: 1.7,
              margin: "0 0 20px",
            }}>
              Work through all 100 questions at your own pace. Reveal answers with instant feedback and detailed rationales.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
              {["Immediate answer feedback", "Full rationale explanations", "Flag questions for review", "Topic navigator"].map(f => (
                <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#526070", fontFamily: "Georgia, serif" }}>
                  <span style={{ color: "#22c55e", fontSize: 14 }}>✓</span> {f}
                </div>
              ))}
            </div>
            <button style={{ ...S.btnPrimary, width: "100%", justifyContent: "center" }}>
              Start Practice →
            </button>
          </div>

          {/* Timed Quiz */}
          <div
            onClick={() => onStart("quiz-config")}
            style={{
              ...S.card,
              cursor: "pointer",
              border: "2px solid transparent",
              transition: "all 0.25s",
              position: "relative",
              overflow: "hidden",
              background: "linear-gradient(160deg, #fff 70%, #fef9ee 100%)",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.border = "2px solid #c9972b";
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(201,151,43,0.2)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.border = "2px solid transparent";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";
            }}
          >
            <div style={{
              position: "absolute", top: 0, right: 0,
              width: 80, height: 80,
              background: "linear-gradient(135deg, #fef3c7, #fde68a)",
              borderRadius: "0 16px 0 80px",
            }} />
            <div style={{ fontSize: 32, marginBottom: 16 }}>⏱</div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 22, fontWeight: 700, margin: "0 0 10px",
              color: "#0d1b2a",
            }}>
              Timed Quiz Mode
            </h2>
            <p style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontSize: 14, color: "#526070", lineHeight: 1.7,
              margin: "0 0 20px",
            }}>
              Simulate real exam conditions with a countdown timer. Receive full performance diagnostics on completion.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
              {["Randomized question order", "Countdown timer", "No hints until submission", "Full diagnostic dashboard"].map(f => (
                <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#526070", fontFamily: "Georgia, serif" }}>
                  <span style={{ color: "#f59e0b", fontSize: 14 }}>✦</span> {f}
                </div>
              ))}
            </div>
            <button style={{ ...S.btnGold, width: "100%", justifyContent: "center" }}>
              Configure Quiz →
            </button>
          </div>
        </div>

        {/* Topic grid */}
        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 17, fontWeight: 600, color: "#0d1b2a", margin: 0,
            }}>
              Topics Covered
            </h3>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {TOPICS.map(t => {
              const c = TOPIC_COLORS[t];
              const count = QUESTIONS.filter(q => q.topic === t).length;
              return (
                <div key={t} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: c.bg, color: c.text,
                  borderRadius: 20, padding: "5px 12px",
                  fontSize: 12, fontFamily: "Georgia, serif", fontWeight: 600,
                }}>
                  {t} <span style={{ opacity: 0.7, fontWeight: 400 }}>({count})</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 48, color: "#94a3b8", fontSize: 12, fontFamily: "Georgia, serif" }}>
          Authored by Amina Beecroft · Vitta Finance Academy · vittafinance.ca
        </div>
      </div>
    </div>
  );
}

// ─── QUIZ CONFIG SCREEN ───────────────────────────────────────────────────────
function QuizConfigScreen({ onBack, onStart }) {
  const [preset, setPreset] = useState(1);

  const p = QUIZ_PRESETS[preset];

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6f9" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e1e8f0", padding: "16px 24px" }}>
        <div style={{ ...S.maxW, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <BrandHeader />
          <button onClick={onBack} style={S.btnGhost}>← Back</button>
        </div>
      </div>

      <div style={{ ...S.maxW, padding: "60px 20px", maxWidth: 600 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 36, fontWeight: 700, margin: "0 0 12px", color: "#0d1b2a",
          }}>
            Configure Your Quiz
          </h1>
          <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: "#526070", fontSize: 16 }}>
            Questions are drawn randomly from the full 100-question bank.
          </p>
        </div>

        <div style={{ ...S.card, marginBottom: 24 }}>
          <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, margin: "0 0 18px", color: "#0d1b2a" }}>
            Select Quiz Length
          </h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {QUIZ_PRESETS.map((pr, i) => (
              <div
                key={i}
                onClick={() => setPreset(i)}
                style={{
                  flex: 1, minWidth: 110,
                  border: preset === i ? "2px solid #1a3050" : "1.5px solid #e1e8f0",
                  borderRadius: 12, padding: "16px 12px",
                  textAlign: "center", cursor: "pointer",
                  background: preset === i ? "#f0f4fa" : "#fff",
                  transition: "all 0.2s",
                }}
              >
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 26, fontWeight: 700,
                  color: preset === i ? "#1a3050" : "#526070",
                }}>
                  {pr.count}
                </div>
                <div style={{ fontSize: 11, color: "#526070", fontFamily: "Georgia, serif", marginBottom: 4 }}>
                  questions
                </div>
                <div style={{
                  ...S.badge(preset === i ? "#1a3050" : "#f4f6f9", preset === i ? "#fff" : "#526070"),
                  fontSize: 10, padding: "2px 8px",
                }}>
                  {pr.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...S.card, marginBottom: 32 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {[
              ["⏱", "Time Limit", `${p.mins} minutes`],
              ["⚡", "Avg per Q", `~${Math.round(p.mins * 60 / p.count)} seconds`],
              ["📊", "Coverage", `${p.count === 100 ? "Full bank" : `${p.count} random Q`}`],
              ["🎯", "Pass Mark", "≥ 60% recommended"],
            ].map(([icon, label, val]) => (
              <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span style={{ fontSize: 22 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "Georgia, serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 16, fontWeight: 600, color: "#0d1b2a" }}>{val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => onStart(p)}
          style={{ ...S.btnGold, width: "100%", justifyContent: "center", padding: "16px", fontSize: 15 }}
        >
          Begin Timed Quiz →
        </button>
      </div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function ProgressBar({ value, max, color = "#1a3050", thin = false }) {
  return (
    <div style={{
      width: "100%", background: "#e8eef5",
      borderRadius: 999, height: thin ? 4 : 7,
      overflow: "hidden",
    }}>
      <div style={{
        width: `${Math.min(100, (value / max) * 100)}%`,
        height: "100%", background: color,
        borderRadius: 999, transition: "width 0.3s ease",
      }} />
    </div>
  );
}

// ─── QUESTION CARD ─────────────────────────────────────────────────────────────
function QuestionCard({
  q, qNum, total, selectedIndex, revealed,
  onSelect, onReveal, onFlag, isFlagged,
  mode, showRationale,
}) {
  const isAnswered = selectedIndex !== undefined && selectedIndex !== null;
  const isCorrect = isAnswered && selectedIndex === q.correct;
  const tc = TOPIC_COLORS[q.topic] || { bg: "#f4f6f9", text: "#526070" };
  const dc = DIFF_COLORS[q.difficulty] || DIFF_COLORS.medium;

  return (
    <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
      {/* Card header */}
      <div style={{
        padding: "18px 28px 14px",
        borderBottom: "1px solid #f0f4fa",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            fontFamily: "'JetBrains Mono', 'Courier New', monospace",
            fontSize: 12, color: "#94a3b8", fontWeight: 600,
          }}>
            {String(qNum).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <div style={S.badge(tc.bg, tc.text)}>{q.topic}</div>
          <div style={S.badge(dc.bg, dc.text)}>
            <span style={{ color: dc.dot, fontSize: 8 }}>●</span>
            {q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1)}
          </div>
        </div>
        <button
          onClick={() => onFlag(q.id)}
          style={{
            ...S.btnGhost,
            border: isFlagged ? "1.5px solid #f59e0b" : "1px solid #e1e8f0",
            color: isFlagged ? "#d97706" : "#94a3b8",
            padding: "5px 12px", fontSize: 12,
            background: isFlagged ? "#fef9ee" : "transparent",
          }}
        >
          {isFlagged ? "🚩 Flagged" : "⚑ Flag"}
        </button>
      </div>

      {/* Question text */}
      <div style={{ padding: "24px 28px 20px" }}>
        <p style={{
          fontFamily: "'Source Serif 4', Georgia, serif",
          fontSize: 16, lineHeight: 1.75, color: "#0d1b2a",
          margin: 0,
        }}>
          {q.text}
        </p>
      </div>

      {/* Options */}
      <div style={{ padding: "0 28px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
        {q.options.map((opt, i) => {
          let bg = "#fff", border = "1.5px solid #e1e8f0", textColor = "#0d1b2a", labelBg = "#f4f6f9", labelColor = "#526070";
          if (revealed && mode === "practice") {
            if (i === q.correct) {
              bg = "#f0fdf4"; border = "1.5px solid #86efac"; textColor = "#166534";
              labelBg = "#22c55e"; labelColor = "#fff";
            } else if (i === selectedIndex) {
              bg = "#fef2f2"; border = "1.5px solid #fca5a5"; textColor = "#991b1b";
              labelBg = "#ef4444"; labelColor = "#fff";
            }
          } else if (i === selectedIndex) {
            bg = "#eff6ff"; border = "1.5px solid #93c5fd"; textColor = "#1e40af";
            labelBg = "#3b82f6"; labelColor = "#fff";
          }

          return (
            <div
              key={i}
              onClick={() => !revealed && onSelect(i)}
              style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: bg, border, borderRadius: 10,
                padding: "13px 16px",
                cursor: revealed ? "default" : "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => {
                if (!revealed && i !== selectedIndex) {
                  e.currentTarget.style.border = "1.5px solid #93c5fd";
                  e.currentTarget.style.background = "#f8fbff";
                }
              }}
              onMouseLeave={e => {
                if (!revealed && i !== selectedIndex) {
                  e.currentTarget.style.border = "1.5px solid #e1e8f0";
                  e.currentTarget.style.background = "#fff";
                }
              }}
            >
              <span style={{
                flexShrink: 0, width: 26, height: 26, borderRadius: 6,
                background: labelBg, color: labelColor,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                transition: "all 0.15s",
              }}>
                {OPTION_LABELS[i]}
              </span>
              <span style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: 14.5, lineHeight: 1.6, color: textColor,
                paddingTop: 3,
              }}>
                {opt}
                {revealed && mode === "practice" && i === q.correct && (
                  <span style={{ marginLeft: 8, fontSize: 13, color: "#22c55e" }}>✓ Correct</span>
                )}
                {revealed && mode === "practice" && i === selectedIndex && i !== q.correct && (
                  <span style={{ marginLeft: 8, fontSize: 13, color: "#ef4444" }}>✗ Incorrect</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Practice: Check / Rationale */}
      {mode === "practice" && (
        <div style={{ padding: "0 28px 24px" }}>
          {!revealed ? (
            <button
              onClick={onReveal}
              disabled={!isAnswered}
              style={{
                ...S.btnPrimary,
                opacity: isAnswered ? 1 : 0.4,
                cursor: isAnswered ? "pointer" : "not-allowed",
                padding: "10px 24px", fontSize: 13,
              }}
            >
              Check Answer
            </button>
          ) : (
            <div style={{
              background: "#fefce8", border: "1px solid #fde68a",
              borderRadius: 10, padding: "14px 18px",
            }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 13, fontWeight: 700, color: "#92400e",
              }}>
                💡 Rationale
              </div>
              <p style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: 14, lineHeight: 1.7, color: "#451a03", margin: 0,
              }}>
                {q.rationale}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PRACTICE MODE ────────────────────────────────────────────────────────────
function PracticeMode({ onExit, onFinish }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [filter, setFilter] = useState("all"); // all, flagged, incorrect
  const [showNav, setShowNav] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);

  const qs = useMemo(() => {
    if (filter === "flagged") return QUESTIONS.filter(q => flagged.has(q.id));
    if (filter === "incorrect") return QUESTIONS.filter((q, i) => revealed[i] && answers[i] !== q.correct);
    return QUESTIONS;
  }, [filter, flagged, revealed, answers]);

  const qIdx = qs.indexOf(qs[current]) >= 0 ? current : 0;
  const q = qs[qIdx] || QUESTIONS[0];
  const globalIdx = QUESTIONS.indexOf(q);

  const answered = Object.keys(answers).length;
  const correct = QUESTIONS.filter((q, i) => answers[i] === q.correct).length;

  const handleSelect = (optIdx) => {
    setAnswers(a => ({ ...a, [globalIdx]: optIdx }));
  };
  const handleReveal = () => {
    setRevealed(r => ({ ...r, [globalIdx]: true }));
  };
  const handleFlag = (id) => {
    setFlagged(f => {
      const nf = new Set(f);
      nf.has(id) ? nf.delete(id) : nf.add(id);
      return nf;
    });
  };
  const go = (dir) => {
    setCurrent(c => Math.max(0, Math.min(qs.length - 1, c + dir)));
  };
  const goTo = (i) => { setCurrent(i); setShowNav(false); };

  const allAnswered = answered === QUESTIONS.length;

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6f9" }}>
      {/* Sticky header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "#fff", borderBottom: "1px solid #e1e8f0",
        padding: "12px 20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
      }}>
        <div style={{
          maxWidth: 900, margin: "0 auto",
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <BrandHeader />
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{
              fontSize: 10, color: "#94a3b8", fontFamily: "Georgia, serif",
              textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4,
            }}>
              Progress · {answered}/{QUESTIONS.length} answered · {correct} correct
            </div>
            <ProgressBar value={answered} max={QUESTIONS.length} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setShowNav(!showNav)}
              style={{ ...S.btnGhost, fontSize: 12 }}
            >
              ☰ Navigator
            </button>
            {allAnswered && (
              <button
                onClick={() => onFinish({ answers, mode: "practice" })}
                style={{ ...S.btnPrimary, padding: "8px 16px", fontSize: 12 }}
              >
                View Results
              </button>
            )}
            <button onClick={onExit} style={{ ...S.btnGhost, fontSize: 12, color: "#ef4444", borderColor: "#fca5a5" }}>
              ✕ Exit
            </button>
          </div>
        </div>

        {/* Navigator panel */}
        {showNav && (
          <div style={{
            maxWidth: 900, margin: "12px auto 0",
            background: "#f8fafc", borderRadius: 12,
            border: "1px solid #e1e8f0", padding: 16,
          }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              {["all", "flagged", "incorrect"].map(f => (
                <button
                  key={f}
                  onClick={() => { setFilter(f); setCurrent(0); }}
                  style={{
                    ...S.btnGhost,
                    background: filter === f ? "#1a3050" : "transparent",
                    color: filter === f ? "#fff" : "#526070",
                    fontSize: 11, padding: "5px 12px",
                    borderColor: filter === f ? "#1a3050" : "#e1e8f0",
                  }}
                >
                  {f === "all" ? `All (${QUESTIONS.length})` : f === "flagged" ? `Flagged (${flagged.size})` : `Incorrect (${QUESTIONS.filter((q, i) => revealed[i] && answers[i] !== q.correct).length})`}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {qs.map((q, i) => {
                const gi = QUESTIONS.indexOf(q);
                const isAns = answers[gi] !== undefined;
                const isRev = revealed[gi];
                const isCorr = isAns && answers[gi] === q.correct;
                const isFlag = flagged.has(q.id);
                let bg = "#f4f6f9", color = "#526070", border = "1px solid #e1e8f0";
                if (isFlag) { bg = "#fef9ee"; color = "#d97706"; border = "1px solid #fde68a"; }
                if (isRev) { bg = isCorr ? "#dcfce7" : "#fee2e2"; color = isCorr ? "#166534" : "#991b1b"; border = `1px solid ${isCorr ? "#86efac" : "#fca5a5"}`; }
                if (i === current) { border = "2px solid #1a3050"; }
                return (
                  <button
                    key={q.id}
                    onClick={() => goTo(i)}
                    style={{
                      width: 34, height: 34, borderRadius: 7,
                      background: bg, color, border,
                      cursor: "pointer",
                      fontSize: 11, fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s",
                    }}
                  >
                    {q.id}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      <div style={{ ...S.maxW, padding: "28px 20px" }}>
        {qs.length === 0 ? (
          <div style={{ ...S.card, textAlign: "center", padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20 }}>
              No questions in this filter
            </h3>
            <button onClick={() => setFilter("all")} style={S.btnPrimary}>View All Questions</button>
          </div>
        ) : (
          <>
            <QuestionCard
              q={q} qNum={qIdx + 1} total={qs.length}
              selectedIndex={answers[globalIdx]}
              revealed={!!revealed[globalIdx]}
              onSelect={handleSelect}
              onReveal={handleReveal}
              onFlag={handleFlag}
              isFlagged={flagged.has(q.id)}
              mode="practice"
            />
            {/* Navigation */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 20, flexWrap: "wrap", gap: 12,
            }}>
              <button
                onClick={() => go(-1)}
                disabled={qIdx === 0}
                style={{ ...S.btnGhost, opacity: qIdx === 0 ? 0.4 : 1 }}
              >
                ← Previous
              </button>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13, color: "#94a3b8",
              }}>
                {qIdx + 1} of {qs.length}
              </span>
              {qIdx < qs.length - 1 ? (
                <button onClick={() => go(1)} style={S.btnPrimary}>
                  Next →
                </button>
              ) : (
                <button onClick={() => onFinish({ answers, mode: "practice" })} style={S.btnGold}>
                  View Results →
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── QUIZ MODE ────────────────────────────────────────────────────────────────
function QuizMode({ config, onExit, onFinish }) {
  const quizQs = useMemo(() => {
    const shuffled = shuffle([...Array(100)].map((_, i) => i));
    return shuffled.slice(0, config.count).map(i => QUESTIONS[i]);
  }, [config.count]);

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(config.mins * 60);
  const [submitted, setSubmitted] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const handleSubmit = useCallback(() => {
    clearInterval(timerRef.current);
    const timeUsed = config.mins * 60 - timeLeft;
    onFinish({ answers, quizQs, mode: "quiz", timeUsed, timeLimit: config.mins * 60 });
  }, [answers, quizQs, timeLeft, config, onFinish]);

  const answered = Object.keys(answers).length;
  const q = quizQs[current];
  const isLowTime = timeLeft < 300;
  const timerColor = isLowTime ? "#dc2626" : timeLeft < 600 ? "#d97706" : "#0d1b2a";

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6f9" }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "#fff", borderBottom: "1px solid #e1e8f0",
        padding: "12px 20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <BrandHeader />
          {/* Timer */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: isLowTime ? "#fef2f2" : "#f0f4fa",
            border: `1.5px solid ${isLowTime ? "#fca5a5" : "#d0dce8"}`,
            borderRadius: 8, padding: "6px 14px",
            transition: "all 0.5s",
          }}>
            <span style={{ fontSize: 14 }}>{isLowTime ? "🔴" : "⏱"}</span>
            <span style={{
              fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              fontSize: 20, fontWeight: 700, color: timerColor,
              letterSpacing: "0.05em",
            }}>
              {fmtTime(timeLeft)}
            </span>
          </div>
          {/* Progress */}
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "Georgia, serif", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
              {answered}/{quizQs.length} answered
            </div>
            <ProgressBar value={answered} max={quizQs.length} />
          </div>
          <button
            onClick={() => setConfirmSubmit(true)}
            style={{ ...S.btnPrimary, padding: "8px 16px", fontSize: 12 }}
          >
            Submit Quiz
          </button>
          <button onClick={onExit} style={{ ...S.btnGhost, fontSize: 12, color: "#ef4444" }}>✕</button>
        </div>
        <div style={{ maxWidth: 900, margin: "4px auto 0" }}>
          <ProgressBar value={current + 1} max={quizQs.length} color="#c9972b" thin />
        </div>
      </div>

      {/* Confirm dialog */}
      {confirmSubmit && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999,
          padding: 20,
        }}>
          <div style={{ ...S.card, maxWidth: 400, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, margin: "0 0 8px" }}>
              Submit Quiz?
            </h3>
            <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: "#526070", fontSize: 14, margin: "0 0 20px" }}>
              You've answered <strong>{answered}</strong> of <strong>{quizQs.length}</strong> questions.
              {answered < quizQs.length && ` ${quizQs.length - answered} unanswered question(s) will be marked incorrect.`}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => setConfirmSubmit(false)} style={S.btnGhost}>Keep Going</button>
              <button onClick={handleSubmit} style={S.btnGold}>Submit & See Results</button>
            </div>
          </div>
        </div>
      )}

      {/* Question */}
      <div style={{ ...S.maxW, padding: "28px 20px" }}>
        <QuestionCard
          q={q} qNum={current + 1} total={quizQs.length}
          selectedIndex={answers[current]}
          revealed={false}
          onSelect={i => setAnswers(a => ({ ...a, [current]: i }))}
          onReveal={() => {}}
          onFlag={() => {}}
          isFlagged={false}
          mode="quiz"
        />

        {/* Navigation */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 12 }}>
          <button onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0} style={{ ...S.btnGhost, opacity: current === 0 ? 0.4 : 1 }}>
            ← Previous
          </button>

          {/* Dot navigator */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxWidth: 400, justifyContent: "center" }}>
            {quizQs.map((_, i) => (
              <div
                key={i}
                onClick={() => setCurrent(i)}
                style={{
                  width: i === current ? 16 : 8, height: 8,
                  borderRadius: 4, cursor: "pointer",
                  background: i === current ? "#1a3050" : answers[i] !== undefined ? "#c9972b" : "#d0dce8",
                  transition: "all 0.2s",
                }}
              />
            ))}
          </div>

          {current < quizQs.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)} style={S.btnPrimary}>Next →</button>
          ) : (
            <button onClick={() => setConfirmSubmit(true)} style={S.btnGold}>Submit →</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── RESULTS SCREEN ───────────────────────────────────────────────────────────
function ResultsScreen({ data, onRetry, onHome }) {
  const { answers, mode, timeUsed, timeLimit } = data;
  const questionList = data.quizQs || QUESTIONS;
  const [reviewFilter, setReviewFilter] = useState("all"); // all, incorrect, correct
  const [expandedQ, setExpandedQ] = useState(null);

  const total = questionList.length;
  const correct = calcScore(answers, questionList);
  const pct = Math.round((correct / total) * 100);
  const passed = pct >= 60;

  const topicStats = useMemo(() => topicBreakdown(answers, questionList), [answers, questionList]);
  const diffStats = useMemo(() => diffBreakdown(answers, questionList), [answers, questionList]);

  const reviewQs = useMemo(() => {
    return questionList.map((q, i) => ({
      q, i,
      selected: answers[i],
      isCorrect: answers[i] === q.correct,
      isUnanswered: answers[i] === undefined,
    })).filter(item => {
      if (reviewFilter === "incorrect") return !item.isCorrect;
      if (reviewFilter === "correct") return item.isCorrect;
      return true;
    });
  }, [questionList, answers, reviewFilter]);

  const scoreColor = pct >= 75 ? "#166534" : pct >= 60 ? "#92400e" : "#991b1b";
  const scoreBg = pct >= 75 ? "#dcfce7" : pct >= 60 ? "#fef3c7" : "#fee2e2";

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6f9" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e1e8f0", padding: "16px 24px" }}>
        <div style={{ ...S.maxW, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <BrandHeader />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onRetry} style={S.btnGhost}>↺ Try Again</button>
            <button onClick={onHome} style={S.btnPrimary}>⌂ Home</button>
          </div>
        </div>
      </div>

      <div style={{ ...S.maxW, padding: "32px 20px" }}>
        {/* Score hero */}
        <div style={{ ...S.card, marginBottom: 24, textAlign: "center", padding: "40px 28px" }}>
          <div style={{
            display: "inline-block",
            background: scoreBg, color: scoreColor,
            borderRadius: 20, padding: "4px 16px",
            fontSize: 12, fontWeight: 700, letterSpacing: "0.1em",
            textTransform: "uppercase", fontFamily: "Georgia, serif",
            marginBottom: 16,
          }}>
            {passed ? "✓ Pass" : "✗ Needs Review"} · Element 1: The Client Relationship
          </div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(56px, 10vw, 80px)",
            fontWeight: 700, color: scoreColor,
            lineHeight: 1, marginBottom: 8,
          }}>
            {pct}%
          </div>
          <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: "#526070", fontSize: 16, marginBottom: 24 }}>
            {correct} correct out of {total} questions
          </div>
          {/* Stats row */}
          <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              ["✓", String(correct), "Correct", "#166534", "#dcfce7"],
              ["✗", String(total - correct), "Incorrect", "#991b1b", "#fee2e2"],
              mode === "quiz" && timeUsed ? ["⏱", fmtTime(timeUsed), "Time Used", "#1e40af", "#dbeafe"] : null,
              mode === "quiz" && timeUsed ? ["⚡", `${Math.round(timeUsed / total)}s`, "Avg / Question", "#92400e", "#fef3c7"] : null,
            ].filter(Boolean).map(([icon, val, label, tc, bg]) => (
              <div key={label} style={{
                background: bg, color: tc, borderRadius: 12, padding: "14px 20px",
                minWidth: 90, textAlign: "center",
              }}>
                <div style={{ fontSize: 20 }}>{icon}</div>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 22, fontWeight: 700,
                }}>
                  {val}
                </div>
                <div style={{ fontSize: 11, fontFamily: "Georgia, serif", opacity: 0.8, letterSpacing: "0.05em" }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Topic + Difficulty breakdown */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 24 }}>
          {/* Topics */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, margin: "0 0 20px", color: "#0d1b2a" }}>
              Performance by Topic
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {TOPICS.filter(t => topicStats[t] && topicStats[t].total > 0).map(t => {
                const st = topicStats[t];
                const tp = Math.round((st.correct / st.total) * 100);
                const c = TOPIC_COLORS[t];
                return (
                  <div key={t}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontFamily: "Georgia, serif", color: "#0d1b2a", fontWeight: 600 }}>{t}</span>
                      <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12, color: tp >= 60 ? "#166534" : "#991b1b", fontWeight: 700,
                      }}>
                        {st.correct}/{st.total} ({tp}%)
                      </span>
                    </div>
                    <div style={{ background: "#f0f4fa", borderRadius: 999, height: 8, overflow: "hidden" }}>
                      <div style={{
                        width: `${tp}%`, height: "100%",
                        background: c.bar, borderRadius: 999, transition: "width 0.8s ease",
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Difficulty */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, margin: "0 0 20px", color: "#0d1b2a" }}>
              Performance by Difficulty
            </h3>
            {[["easy", "Easy"], ["medium", "Medium"], ["hard", "Hard"]].map(([key, label]) => {
              const st = diffStats[key];
              const tp = st.t > 0 ? Math.round((st.c / st.t) * 100) : 0;
              const dc = DIFF_COLORS[key];
              return (
                <div key={key} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: dc.dot, fontSize: 12 }}>●</span>
                      <span style={{ fontSize: 13, fontFamily: "Georgia, serif", color: "#0d1b2a", fontWeight: 600 }}>{label}</span>
                    </div>
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12, color: tp >= 60 ? "#166534" : "#991b1b", fontWeight: 700,
                    }}>
                      {st.c}/{st.t} ({tp}%)
                    </span>
                  </div>
                  <div style={{ background: "#f0f4fa", borderRadius: 999, height: 10, overflow: "hidden" }}>
                    <div style={{
                      width: `${tp}%`, height: "100%",
                      background: dc.dot, borderRadius: 999, transition: "width 0.8s ease",
                    }} />
                  </div>
                </div>
              );
            })}

            {/* Study tip */}
            {diffStats.hard.t > 0 && diffStats.hard.c / diffStats.hard.t < 0.6 && (
              <div style={{
                background: "#fef9ee", border: "1px solid #fde68a",
                borderRadius: 10, padding: "12px 14px", marginTop: 8,
              }}>
                <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13, color: "#92400e", margin: 0, lineHeight: 1.6 }}>
                  💡 Focus on <strong>Hard</strong> questions — you scored {Math.round(diffStats.hard.c / diffStats.hard.t * 100)}% in that category. Review the rationales for your incorrect hard questions below.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Review section */}
        <div style={{ ...S.card }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, margin: 0, color: "#0d1b2a" }}>
              Question Review
            </h3>
            <div style={{ display: "flex", gap: 8 }}>
              {["all", "incorrect", "correct"].map(f => (
                <button
                  key={f}
                  onClick={() => setReviewFilter(f)}
                  style={{
                    ...S.btnGhost,
                    background: reviewFilter === f ? "#1a3050" : "transparent",
                    color: reviewFilter === f ? "#fff" : "#526070",
                    borderColor: reviewFilter === f ? "#1a3050" : "#e1e8f0",
                    fontSize: 11, padding: "5px 12px",
                  }}
                >
                  {f === "all" ? `All (${total})` : f === "incorrect" ? `Incorrect (${total - correct})` : `Correct (${correct})`}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {reviewQs.map(({ q, i, selected, isCorrect, isUnanswered }) => (
              <div key={q.id}
                style={{
                  border: `1px solid ${isCorrect ? "#86efac" : "#fca5a5"}`,
                  borderRadius: 10,
                  background: isCorrect ? "#f0fdf4" : "#fef2f2",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    display: "flex", alignItems: "flex-start", gap: 12,
                    cursor: "pointer",
                  }}
                  onClick={() => setExpandedQ(expandedQ === q.id ? null : q.id)}
                >
                  <span style={{
                    flexShrink: 0, fontSize: 16,
                    marginTop: 1,
                  }}>
                    {isUnanswered ? "○" : isCorrect ? "✓" : "✗"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap",
                    }}>
                      <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11, color: "#94a3b8",
                      }}>
                        Q{q.id}
                      </span>
                      <div style={S.badge(TOPIC_COLORS[q.topic]?.bg || "#f4f6f9", TOPIC_COLORS[q.topic]?.text || "#526070")}>
                        {q.topic}
                      </div>
                      <div style={S.badge(DIFF_COLORS[q.difficulty].bg, DIFF_COLORS[q.difficulty].text)}>
                        {q.difficulty}
                      </div>
                    </div>
                    <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13, color: "#0d1b2a", margin: 0, lineHeight: 1.5 }}>
                      {q.text.length > 120 ? q.text.slice(0, 120) + "…" : q.text}
                    </p>
                  </div>
                  <span style={{ flexShrink: 0, color: "#94a3b8", fontSize: 12 }}>
                    {expandedQ === q.id ? "▲" : "▼"}
                  </span>
                </div>
                {expandedQ === q.id && (
                  <div style={{ padding: "0 16px 16px 44px" }}>
                    <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13.5, color: "#0d1b2a", margin: "0 0 10px", lineHeight: 1.7 }}>
                      {q.text}
                    </p>
                    {q.options.map((opt, oi) => {
                      let bg = "#fff", border = "1px solid #e1e8f0", color = "#0d1b2a";
                      if (oi === q.correct) { bg = "#f0fdf4"; border = "1px solid #86efac"; color = "#166534"; }
                      else if (oi === selected) { bg = "#fef2f2"; border = "1px solid #fca5a5"; color = "#991b1b"; }
                      return (
                        <div key={oi} style={{
                          display: "flex", gap: 8, background: bg, border, borderRadius: 8,
                          padding: "8px 12px", marginBottom: 6,
                        }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color, fontWeight: 700, flexShrink: 0 }}>
                            {OPTION_LABELS[oi]}
                          </span>
                          <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13, color, lineHeight: 1.5 }}>
                            {opt}
                            {oi === q.correct && <span style={{ marginLeft: 6, fontSize: 11 }}>✓ Correct Answer</span>}
                            {oi === selected && oi !== q.correct && <span style={{ marginLeft: 6, fontSize: 11 }}>✗ Your Answer</span>}
                          </span>
                        </div>
                      );
                    })}
                    <div style={{
                      background: "#fefce8", border: "1px solid #fde68a",
                      borderRadius: 8, padding: "10px 14px", marginTop: 8,
                    }}>
                      <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 12, fontWeight: 700, color: "#92400e" }}>
                        💡 Rationale:
                      </span>
                      <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13, color: "#451a03", marginLeft: 8, lineHeight: 1.6 }}>
                        {q.rationale}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 48, color: "#94a3b8", fontSize: 12, fontFamily: "Georgia, serif" }}>
          Vitta Finance Academy · Element 1 of 8 · vittafinance.ca
        </div>
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  useFonts();

  const [screen, setScreen] = useState("home"); // home | practice | quiz-config | quiz | results
  const [quizConfig, setQuizConfig] = useState(null);
  const [results, setResults] = useState(null);

  const handleStart = (mode) => {
    if (mode === "practice") setScreen("practice");
    else if (mode === "quiz-config") setScreen("quiz-config");
  };

  const handleQuizStart = (config) => {
    setQuizConfig(config);
    setScreen("quiz");
  };

  const handleFinish = (data) => {
    setResults(data);
    setScreen("results");
  };

  const handleRetry = () => {
    setResults(null);
    if (results?.mode === "quiz") setScreen("quiz-config");
    else setScreen("home");
  };

  return (
    <div style={S.root}>
      {screen === "home" && <HomeScreen onStart={handleStart} />}
      {screen === "practice" && (
        <PracticeMode
          onExit={() => setScreen("home")}
          onFinish={handleFinish}
        />
      )}
      {screen === "quiz-config" && (
        <QuizConfigScreen
          onBack={() => setScreen("home")}
          onStart={handleQuizStart}
        />
      )}
      {screen === "quiz" && quizConfig && (
        <QuizMode
          config={quizConfig}
          onExit={() => setScreen("home")}
          onFinish={handleFinish}
        />
      )}
      {screen === "results" && results && (
        <ResultsScreen
          data={results}
          onRetry={handleRetry}
          onHome={() => { setResults(null); setScreen("home"); }}
        />
      )}
    </div>
  );
}
